import styled, { css } from 'styled-components';

import { BAR_COLORS, COLORS, SPACE } from '../../../../theme/tokens';
import { Text } from '../../../../components';

/**
 * One mailbox tile (Figma OWS-Flow 46:877). ONE fixed skeleton for every state: a 32px head row,
 * a sub-line reserved at exactly two lines, the storage block, and a footer row, so a grid row
 * never changes height when a card's state changes. The slots are fixed heights, not min-heights, on
 * purpose; that is the property the CT test measures.
 *
 * Deliberately NOT Scenario A's full-width card layout (address left, pill + kebab right, labelled
 * columns): these are ~300px tiles in a two-column grid, so the facts stack instead of columning.
 * The typography and tokens are the house ones; the layout is the tile's own. Do not "fix" it back
 * into columns.
 *
 * `$muted` (a deleted mailbox) mutes every piece of text on the tile (address included) and sits it
 * on a recessed surface: no fill, a faint border, exactly as the spec sheet draws it.
 */
/**
 * ONE height for every tile in the grid, add-tiles included (AddTiles.styles imports it): Figma
 * 46:877 draws all six cards at 210 (mailbox tiles 209), and a first row with two different
 * heights is visible every time. A BORDER-BOX height (both tiles set `box-sizing: border-box`; the
 * area has no global reset, and on a content-box tile a min-height silently gains the padding and
 * border). The repo's value is higher than Figma's by exactly the sub-line's second row plus the
 * address-to-sub-line gap, derived once from the rendered boxes and never hand-tuned:
 *
 *   border-top                  1
 *   padding-top                16
 *   head row                   32
 *   gap (address -> sub-line)   4   (Figma: 0; asked for explicitly, see TopGroup)
 *   sub-line                   40   (Figma: 21, one line; see SubLine for why two)
 *   gap                        20
 *   storage block              64   (label 16.5 + 3 + value 21 + 8 + 3 + bar 12, snapped by layout)
 *   gap                        20
 *   footer                     20
 *   padding-bottom             16
 *   border-bottom               1
 *                             -----
 *                             234
 */
export const MAILBOX_TILE_MIN_HEIGHT = 234;

export const Tile = styled.div<{ $muted: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[20]};
  padding: ${SPACE[16]} ${SPACE[20]};
  border-radius: 14px;
  min-width: 0;
  box-sizing: border-box;
  min-height: ${MAILBOX_TILE_MIN_HEIGHT}px;
  background: ${(p) => (p.$muted ? 'transparent' : COLORS.surfaceFaint)};
  /* The muted border is one step brighter than the panel border, the spec sheet's recessed
     outline; every other state has no border at all. */
  border: 1px solid ${(p) => (p.$muted ? COLORS.surfaceMuted : 'transparent')};
`;

/**
 * The address row and its sub-line, as ONE unit: the sub-line belongs to the address, so the gap
 * between them is SPACE[4], not the tile's 20px block gap. Figma has the sub-line flush against the
 * 32px head row (0px; the 22.5px address is optically centred in 32, so it reads as about 4). 4px
 * was asked for explicitly; do not reopen it on the next Figma diff.
 */
export const TopGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  min-width: 0;
`;

/** The head: address on the left, the pill slot on the right (ONE pill: a status when one applies,
 *  else the slot word). Fixed at 32px (Figma) so the row's geometry never changes. */
export const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE[12]};
  height: 32px;
  min-width: 0;
`;

/** The full mailbox address as the tile's heading; truncates rather than wrapping. */
export const Address = styled.span<{ $muted: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 15px;
  font-weight: 700;
  line-height: 22.5px;
  color: ${(p) => (p.$muted ? COLORS.textFaint : COLORS.textStrong)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** The head slot's right-hand cell. Always rendered so the row keeps its geometry when empty. */
export const HeadSlot = styled.span`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  min-height: 24px;
`;

/** The shared shape of the tile's text links: Webmail, Setup Mailbox.
 *  13px semibold with a 16px glyph 6px after it (the link's own glyph geometry, off-scale). */
const linkBase = css`
  appearance: none;
  display: inline-flex;
  align-items: center;
  /* Off-scale: 6px is the label->glyph micro-gap of a link, glyph geometry not layout. */
  gap: 6px;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  line-height: 19.5px;
  text-decoration: none;
  white-space: nowrap;
`;

/** A footer link (Setup Mailbox, Webmail). */
export const FooterLink = styled.button`
  ${linkBase}
  color: ${COLORS.iconBright};

  &:hover:not(:disabled) {
    color: ${COLORS.textStrong};
  }

  /* Present but unusable (the pending support@ card): muted, no hover, no pointer. */
  &:disabled {
    color: ${COLORS.textFaint};
    cursor: default;
  }
`;

/** The footer link's glyph: the area's chevron-down turned to point right. */
export const Chevron = styled.span`
  display: inline-flex;
  align-items: center;
  transform: rotate(-90deg);
`;

/** The status pill. Colour arrives per state via `$bg`/`$fg`. */
export const StatusPill = styled.span<{ $bg: string; $fg: string }>`
  display: inline-flex;
  align-items: center;
  /* Off-scale by design: the 10px inset is the pill's internal geometry against its 24px height, not
     layout spacing. A pill is a word and its colour: no glyph. */
  height: 24px;
  padding: 0 10px;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  color: ${(p) => p.$fg};
  background: ${(p) => p.$bg};
`;

/** The sub-line tones. `pending` is the line on a Pending card saying why Setup Mailbox and Webmail
 *  wait; `muted` is kept for a state that may come back. */
export type SubLineTone = 'muted' | 'pending';

const SUB_LINE_COLOR: Record<SubLineTone, string> = {
  muted: COLORS.textMuted,
  pending: COLORS.textSubtle,
};

/**
 * The ONE sub-line under the address, reserved at EXACTLY two lines (a fixed height with a 2-line
 * clamp, not a min-height): a tile with no line must be the same height as one carrying one.
 * `$muted` is a deleted tile, where the whole tile fades.
 *
 * THE ONE PLACE WE DO NOT FOLLOW FIGMA: 46:877 reserves one line (21px). Its tiles are 444px wide;
 * at the Settings content cap ours are about 300px, where the pending line wraps to two. A one-line
 * reservation would make a waiting tile grow and the row jump, the exact thing this fixed skeleton
 * exists to prevent. Two lines, 40px, on purpose.
 */
export const SubLine = styled(Text)<{ $tone: SubLineTone; $muted: boolean }>`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  height: 40px;
  font-size: 13px;
  line-height: 20px;
  color: ${(p) => (p.$muted ? COLORS.textFaint : SUB_LINE_COLOR[p.$tone])};
`;

/** The tile's BOTTOM block (the figures, the bar, the footer), pinned to the bottom edge whatever the
 *  content above it, so the two edges of every card in a row line up. */
export const Bottom = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[20]};
  margin-top: auto;
  min-width: 0;
`;

/** The figures and the bar under them. */
export const Storage = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

/** The figures row: Storage on the left, Messages right-aligned. */
export const Figures = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACE[12]};
  padding-bottom: ${SPACE[8]};
  min-width: 0;
`;

/** One figure: the uppercase label over its value. The 3px label->value gap is the tight pairing of
 *  one field (off-scale, like the house metric cells). `$end` right-aligns it (Messages). */
export const Figure = styled.div<{ $end?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$end ? 'flex-end' : 'flex-start')};
  gap: 3px;
  min-width: 0;
`;

export const StorageLabel = styled(Text)<{ $muted: boolean }>`
  font-size: 11px;
  line-height: 16.5px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${COLORS.textFaint};
  opacity: ${(p) => (p.$muted ? 0.8 : 1)};
`;

export const StorageValue = styled.span<{ $muted: boolean }>`
  font-size: 14px;
  line-height: 21px;
  color: ${(p) => (p.$muted ? COLORS.textFaint : COLORS.eyebrowDetail)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * The storage bar, 12px and flat (the spec sheet's treatment). Written here rather than reusing the
 * shared `ProgressBar`: that one is an 8px pill that animates its fill on mount and carries no
 * ARIA, and there is nothing yet for a fill animation to be anchored to. No transition on purpose.
 */
export const Bar = styled.div<{ $muted: boolean }>`
  position: relative;
  height: 12px;
  width: 100%;
  overflow: hidden;
  background: ${COLORS.surfaceMuted};
  opacity: ${(p) => (p.$muted ? 0.6 : 1)};
`;

export const BarFill = styled.div<{ $ratio: number; $tone: 'info' | 'warning' }>`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${(p) => Math.round(p.$ratio * 1000) / 10}%;
  background: ${(p) => BAR_COLORS[p.$tone]};
`;

/** The bar's slot when there is no bar to draw (no ceiling reported, no usage to read, or the
 *  usage not answered yet, when it holds the skeleton): the bar's height, so the tile keeps the
 *  height of its neighbours. */
export const BarSlot = styled.div`
  height: 12px;
`;

/** The footer: two fixed slots above a hairline, left constant, right the state action. Fixed at
 *  the link's line height so an empty right slot changes nothing. */
export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE[20]};
  height: 20px;
`;

/** Either footer cell; the right one is always rendered so the row's geometry never depends on
 *  whether a link is there. */
export const FooterSlot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[16]};
  min-width: 0;
`;
